/**
 * Accounting API service
 */
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, getValidAccessToken } from '../../../shared/api/axiosInstance';
import { API_BASE_URL } from '../../../shared/api/config';
import { buildParams } from '../../../shared/api/buildParams';
import type {
  CurrencyRevaluation,
  FxRevaluationListResponse,
  ListFxRevaluationsParams,
} from '../types/fxRevaluation';
import type { VendorRuleUpdateRequest } from '@/generated/api/models/vendorRuleUpdateRequest';
import type { VendorRuleDocumentType } from '@/generated/api/models/vendorRuleDocumentType';
import type { ConsolidationEliminationReport } from '@/generated/api/models/consolidationEliminationReport';
import {
  Account,
  AccountCreate,
  AccountUpdate,
  AccountBalanceReport,
  FiscalPeriod,
  FiscalPeriodCreate,
  GenerateFiscalYearRequest,
  JournalEntry,
  JournalEntryCreate,
  JournalEntryUpdate,
  JournalEntryReverse,
  Currency,
  CurrencyCreate,
  ExchangeRate,
  ExchangeRateCreate,
  FxReferenceRate,
  FxRatePolicy,
  FxRatePolicyListData,
  FxRatePolicyUpsert,
  FxDefaultMarkupUpdate,
  TrialBalanceReport,
  BalanceSheetReport,
  IncomeStatementReport,
  GeneralLedgerReport,
  PaginatedResponse,
  DataResponse,
  ListAccountsParams,
  ListJournalEntriesParams,
  ListFiscalPeriodsParams,
  FriendlyReportParams,
  InvoiceSummaryReport as InvoiceSummaryReportType,
  BillSummaryReport as BillSummaryReportType,
  ProfitLossReport as ProfitLossReportType,
  TrendReport as TrendReportData,
  TrendReportParams,
  AccountSuggestion,
  AdvancedReportParams,
  SummarizedIncomeReport,
  SummarizedExpensesReport,
  IncomeByCustomerReport,
  ExpensesByVendorReport,
  ExpensesAndPaymentsByVendorReport,
  CollectionReport,
  CollectionSummaryReport,
  ComparativePLReport,
  VATReport,
  WithholdingTaxCustomerReport,
  WithholdingTaxVendorReport,
  ExpenseWithholdingSummaryReport,
  PL6111Report,
  CashFlowStatementReport,
  CashFlowTimeseriesReport,
  CoaImportPreview,
  CoaImportResult,
  ChartStatus,
  Dimension,
  DimensionCreate,
  DimensionUpdate,
  DimensionValue,
  DimensionValueCreate,
  DimensionValueUpdate,
  DimensionDefaultRule,
  DimensionDefaultRuleCreate,
  DimensionDefaultRuleUpdate,
  ListDimensionDefaultRulesParams,
} from '../types';

const API_BASE = '/api/v1/accounting';

// File attachments (upload / list / delete / download) are served by the shared
// file_management seam — use `fileApi` from `shared/files/services/fileApi`
// (display via `useAuthenticatedImageUrl`, bytes via `fileApi.download` /
// `fileApi.fetchAsBlob`). The old module-local `fileApi`/`FileRecord` here
// bypassed that seam (raw authed `/download` URLs that 401 without a bearer) and
// have been removed.

// ============== AI Document Parsing ==============

// Re-exported from the generated config binding (shared/config/document_type
// .config.json → shared/schemas/enums/document_type.schema.json) rather than
// re-typed, so the ingestion rule dialog's option list cannot drift from it.
// Imported as well as re-exported — a bare `export … from` does not bind the
// name locally, and `StoredIngestionRuleActions` below reads it.
import type { DocumentType } from '../../../generated/config-enums';

export type { DocumentType };
export type JeDetailLevel = 'summary' | 'grouped' | 'detailed';
/**
 * For doc_type='receipt' only.
 * - 'treat_as_paid_bill' (default): receipt is a fully-settled expense; finalize
 *   creates a paid-immediately Bill — JE: DR Expense / CR Bank.
 * - 'bill_will_follow': receipt is an advance/prepayment; finalize creates a
 *   Payment on account — JE: DR Accounts Payable / CR Bank. The vendor's AP
 *   carries a debit balance until a matching invoice arrives.
 */
export type ReceiptTreatment = 'treat_as_paid_bill' | 'bill_will_follow';

export const aiApi = {
  suggestAccount: async (
    companyName: string,
    accountType: 'expense' | 'revenue',
    companyIndustry?: string
  ): Promise<DataResponse<AccountSuggestion>> => {
    return apiPost(`${API_BASE}/suggest-account`, {
      company_name: companyName,
      account_type: accountType,
      company_industry: companyIndustry || undefined,
    });
  },
};

// ============== Accounts ==============

export interface AccountNumberSuggestion {
  suggested_number: string | null;
  range: { start: number; end: number };
  band: { start: number; end: number };
  categories: Array<{
    parent_account_id: string | null;
    // Stable key for a standard expense sub-category (frontend maps it to a
    // translated name); null for a tenant header account, whose `label` is its
    // own name and is displayed as-is (via BidiText).
    category_key: string | null;
    label: string;
    anchor: number;
    range_start: number;
    range_end: number;
  }>;
}

export interface ExpenseAccountSuggestionNewAccount {
  name: string;
  account_type: string;
  normal_balance: string;
  category: { range_start: number; range_end: number };
  sub_group_anchor: number | null;
  suggested_number: string | null;
  reasoning?: string | null;
}

export interface ExpenseAccountSuggestionExistingRef {
  account_id: string;
  account_number: string;
  name: string;
}

export type ExpenseAccountRecommendation =
  | {
      action: 'use_existing';
      existing: ExpenseAccountSuggestionExistingRef;
      reasoning?: string | null;
    }
  | { action: 'create_new'; new_account: ExpenseAccountSuggestionNewAccount };

export interface ExpenseAccountSuggestion {
  recommendation: ExpenseAccountRecommendation;
  alternatives: Array<{ action: 'create_new'; new_account: ExpenseAccountSuggestionNewAccount }>;
  close_existing: ExpenseAccountSuggestionExistingRef[];
}

export const accountApi = {
  list: async (params?: ListAccountsParams): Promise<PaginatedResponse<Account> | DataResponse<Account[]>> => {
    const url = `${API_BASE}/accounts${buildParams(params)}`;
    return apiGet<PaginatedResponse<Account> | DataResponse<Account[]>>(url);
  },

  get: async (id: string, includeBalance?: boolean): Promise<DataResponse<Account>> => {
    const url = `${API_BASE}/accounts/${id}${buildParams({ include_balance: includeBalance })}`;
    return apiGet<DataResponse<Account>>(url);
  },

  create: async (data: AccountCreate): Promise<DataResponse<Account>> => {
    return apiPost<DataResponse<Account>>(`${API_BASE}/accounts`, data);
  },

  update: async (id: string, data: AccountUpdate): Promise<DataResponse<Account>> => {
    return apiPut<DataResponse<Account>>(`${API_BASE}/accounts/${id}`, data);
  },

  delete: async (id: string): Promise<void | DataResponse<Account>> => {
    // Note: This endpoint may return 204 or a deactivation response
    // Using apiDelete which doesn't return data, but the endpoint might return JSON
    await apiDelete(`${API_BASE}/accounts/${id}`);
  },

  initializeDefault: async (): Promise<DataResponse<{ account_count: number }>> => {
    return apiPost<DataResponse<{ account_count: number }>>(`${API_BASE}/accounts/initialize-default`);
  },

  getBalance: async (id: string, asOfDate?: string): Promise<DataResponse<AccountBalanceReport>> => {
    const url = `${API_BASE}/accounts/${id}/balance${buildParams({ as_of_date: asOfDate })}`;
    return apiGet<DataResponse<AccountBalanceReport>>(url);
  },

  /**
   * One page of an account's ledger. `count` is the unpaged total of the same
   * filtered set — the page drives its pager from that, never from `data.length`
   * (docs/design-system/ui-list-pages.md § 11).
   */
  getTransactions: async (
    id: string,
    params?: { start_date?: string; end_date?: string; limit?: number; offset?: number }
  ): Promise<DataResponse<unknown[]> & { count: number }> => {
    const url = `${API_BASE}/accounts/${id}/transactions${buildParams(params)}`;
    return apiGet<DataResponse<unknown[]> & { count: number }>(url);
  },

  getStatus: async (): Promise<DataResponse<ChartStatus>> => {
    return apiGet<DataResponse<ChartStatus>>(`${API_BASE}/accounts/status`);
  },

  // Deterministic (no-AI) next-number + category suggestion for the Create
  // Account dialog. Re-fetched when the account type / parent changes.
  numberSuggestion: async (params: {
    account_type: string;
    parent_account_id?: string | null;
    company_id?: string | null;
  }): Promise<DataResponse<AccountNumberSuggestion>> => {
    return apiGet<DataResponse<AccountNumberSuggestion>>(
      `${API_BASE}/accounts/number-suggestion${buildParams(params)}`,
    );
  },

  // One-call AI expense-account suggestion for the smart Create Account dialog.
  suggestExpenseAccount: async (body: {
    description: string;
    vendor_name?: string | null;
    amount?: number | null;
    currency_code?: string | null;
    company_id?: string | null;
  }): Promise<DataResponse<ExpenseAccountSuggestion>> => {
    return apiPost<DataResponse<ExpenseAccountSuggestion>>(
      `${API_BASE}/accounts/suggest-expense-account`,
      body,
    );
  },

  initializeCustomized: async (data?: {
    business_models: Record<string, boolean>;
    industry_verticals: string[];
    business_type?: string;
  }): Promise<DataResponse<{ account_count: number; accounts_summary: Record<string, number> }>> => {
    return apiPost<DataResponse<{ account_count: number; accounts_summary: Record<string, number> }>>(
      `${API_BASE}/accounts/initialize`,
      data
    );
  },

  resetChart: async (): Promise<DataResponse<{
    deleted_count: number;
    retained_count: number;
    retained_accounts: Array<{ account_number: string; name: string }>;
  }>> => {
    return apiPost<DataResponse<{
      deleted_count: number;
      retained_count: number;
      retained_accounts: Array<{ account_number: string; name: string }>;
    }>>(`${API_BASE}/accounts/reset`);
  },

  resetAllChartData: async (stepUpProof: string): Promise<DataResponse<{
    deleted_count: number;
    deleted_draft_entry_count: number;
    retained_posted_entry_count: number;
  }>> => {
    return apiPost<DataResponse<{
      deleted_count: number;
      deleted_draft_entry_count: number;
      retained_posted_entry_count: number;
    }>>(`${API_BASE}/accounts/reset-all`, { step_up_proof: stepUpProof });
  },

  /**
   * Set the active company to inherit its parent's effective chart of accounts.
   * Creates no account rows — used by the "Use {parent}'s chart" setup action.
   */
  inherit: async (): Promise<DataResponse<ChartStatus>> => {
    return apiPost<DataResponse<ChartStatus>>(`${API_BASE}/accounts/inherit`);
  },

  exportFile: async (format: string, companyId?: string): Promise<Blob> => {
    const qs = buildParams({ format, company_id: companyId });
    const token = await getValidAccessToken();
    const res = await fetch(`${API_BASE_URL}${API_BASE}/accounts/export${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to export accounts: ${res.statusText}`);
    return res.blob();
  },

  importPreview: async (file: File): Promise<DataResponse<CoaImportPreview>> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost<DataResponse<CoaImportPreview>>(`${API_BASE}/accounts/import/preview`, formData);
  },

  importFile: async (
    file: File,
    mappings: Record<string, string>,
    companyId?: string
  ): Promise<DataResponse<CoaImportResult>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mappings', JSON.stringify(mappings));
    if (companyId) formData.append('company_id', companyId);
    return apiPost<DataResponse<CoaImportResult>>(`${API_BASE}/accounts/import`, formData);
  },
};

// ============== Opening AR/AP Balance Import ==============
// side='ar' → opening customer invoices; side='ap' → opening vendor bills. The
// preview/import responses share the generic tabular shape (CoaImport* types).

export type OpeningBalanceSide = 'ar' | 'ap';

export const openingBalanceApi = {
  importPreview: async (
    side: OpeningBalanceSide,
    file: File
  ): Promise<DataResponse<CoaImportPreview>> => {
    const formData = new FormData();
    formData.append('file', file);
    // `side` is the closed union 'ar' | 'ap' and the backend registers
    // /ar/import/preview and /ap/import/preview as literals, so the dynamic segment
    // IS a route name — the case the escape below exists for.
    // frontend-api-route-link-ok: side is 'ar' | 'ap', both registered as literals
    return apiPost<DataResponse<CoaImportPreview>>(
      `${API_BASE}/opening-balances/${side}/import/preview`,
      formData
    );
  },

  importFile: async (
    side: OpeningBalanceSide,
    params: {
      file: File;
      mappings: Record<string, string>;
      cutoffDate: string;
      sourceSystem: string;
      companyId?: string;
    }
  ): Promise<DataResponse<CoaImportResult>> => {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('mappings', JSON.stringify(params.mappings));
    formData.append('cutoff_date', params.cutoffDate);
    formData.append('source_system', params.sourceSystem);
    if (params.companyId) formData.append('company_id', params.companyId);
    // Same closed union as the preview call above.
    // frontend-api-route-link-ok: side is 'ar' | 'ap', both registered as literals
    return apiPost<DataResponse<CoaImportResult>>(
      `${API_BASE}/opening-balances/${side}/import`,
      formData
    );
  },
};

// ============== Journal Entries ==============

export const journalEntryApi = {
  list: async (params?: ListJournalEntriesParams): Promise<PaginatedResponse<JournalEntry>> => {
    const url = `${API_BASE}/journal-entries${buildParams(params)}`;
    return apiGet<PaginatedResponse<JournalEntry>>(url);
  },

  get: async (id: string): Promise<DataResponse<JournalEntry>> => {
    return apiGet<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries/${id}`);
  },

  create: async (data: JournalEntryCreate): Promise<DataResponse<JournalEntry>> => {
    return apiPost<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries`, data);
  },

  update: async (id: string, data: JournalEntryUpdate): Promise<DataResponse<JournalEntry>> => {
    return apiPut<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries/${id}`, data);
  },

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/journal-entries/${id}`);
  },

  post: async (id: string): Promise<DataResponse<JournalEntry>> => {
    return apiPost<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries/${id}/post`);
  },

  reverse: async (
    id: string,
    data: JournalEntryReverse,
  ): Promise<DataResponse<JournalEntry>> => {
    return apiPost<DataResponse<JournalEntry>>(`${API_BASE}/journal-entries/${id}/reverse`, data);
  },
};

// ============== Fiscal Periods ==============

export const fiscalPeriodApi = {
  list: async (params?: ListFiscalPeriodsParams): Promise<PaginatedResponse<FiscalPeriod>> => {
    const url = `${API_BASE}/fiscal-periods${buildParams(params)}`;
    return apiGet<PaginatedResponse<FiscalPeriod>>(url);
  },

  get: async (id: string): Promise<DataResponse<FiscalPeriod>> => {
    return apiGet<DataResponse<FiscalPeriod>>(`${API_BASE}/fiscal-periods/${id}`);
  },

  create: async (data: FiscalPeriodCreate): Promise<DataResponse<FiscalPeriod>> => {
    return apiPost<DataResponse<FiscalPeriod>>(`${API_BASE}/fiscal-periods`, data);
  },

  generateYear: async (data: GenerateFiscalYearRequest): Promise<DataResponse<FiscalPeriod[]>> => {
    return apiPost<DataResponse<FiscalPeriod[]>>(`${API_BASE}/fiscal-periods/generate-year`, data);
  },

  // close/reopen deliberately absent: a close runs the month-end readiness
  // checklist first and records an audit artifact, so it lives on the generated
  // /accounting/period-close client (see hooks/usePeriodClose.ts). Locking is
  // still a plain fiscal-calendar act.

  lock: async (id: string): Promise<DataResponse<FiscalPeriod>> => {
    return apiPost<DataResponse<FiscalPeriod>>(`${API_BASE}/fiscal-periods/${id}/lock`);
  },
};

// ============== Currencies ==============

export const currencyApi = {
  list: async (isActive?: boolean): Promise<DataResponse<Currency[]>> => {
    const url = `${API_BASE}/currencies${buildParams({ is_active: isActive })}`;
    return apiGet<DataResponse<Currency[]>>(url);
  },

  create: async (data: CurrencyCreate): Promise<DataResponse<Currency>> => {
    return apiPost<DataResponse<Currency>>(`${API_BASE}/currencies`, data);
  },

  initializeDefault: async (): Promise<DataResponse<Currency[]>> => {
    return apiPost<DataResponse<Currency[]>>(`${API_BASE}/currencies/initialize-default`);
  },
};

// ============== Exchange Rates ==============

export const exchangeRateApi = {
  list: async (params?: {
    from_currency?: string;
    to_currency?: string;
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }): Promise<DataResponse<ExchangeRate[]>> => {
    const url = `${API_BASE}/exchange-rates${buildParams(params)}`;
    return apiGet<DataResponse<ExchangeRate[]>>(url);
  },

  create: async (data: ExchangeRateCreate): Promise<DataResponse<ExchangeRate>> => {
    return apiPost<DataResponse<ExchangeRate>>(`${API_BASE}/exchange-rates`, data);
  },

  delete: async (rateId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/exchange-rates/${rateId}`);
  },

  convert: async (params: {
    amount: number;
    from_currency: string;
    to_currency: string;
    rate_date?: string;
  }): Promise<DataResponse<{ amount: number; from_currency: string; to_currency: string; converted_amount: string; rate: string }>> => {
    const url = `${API_BASE}/exchange-rates/convert${buildParams(params)}`;
    return apiGet<DataResponse<{ amount: number; from_currency: string; to_currency: string; converted_amount: string; rate: string }>>(url);
  },
};

// ============== FX Reference Rates (global market rates, read-only) ==============

export const referenceRateApi = {
  list: async (params?: {
    base_currency?: string;
    quote_currency?: string;
    latest?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<DataResponse<FxReferenceRate[]>> => {
    const url = `${API_BASE}/fx-reference-rates${buildParams(params)}`;
    return apiGet<DataResponse<FxReferenceRate[]>>(url);
  },
};

// ============== FX Rate Policies (one policy per directed currency pair) ==============

export const fxRatePolicyApi = {
  // Every pair the tenant tracks, merged with the live market rate, plus the
  // standing tenant-wide default markup that `default`-mode pairs inherit.
  list: async (): Promise<DataResponse<FxRatePolicyListData>> => {
    return apiGet<DataResponse<FxRatePolicyListData>>(`${API_BASE}/fx-rate-policies`);
  },

  // Set a pair's single mode (idempotent per pair). The backend clears whichever
  // store the chosen mode does not use, so exactly one rule governs the pair.
  upsert: async (data: FxRatePolicyUpsert): Promise<DataResponse<FxRatePolicy>> => {
    return apiPost<DataResponse<FxRatePolicy>>(`${API_BASE}/fx-rate-policies`, data);
  },

  // Stop tracking a pair: removes its markup row AND its pinned rates.
  delete: async (fromCurrency: string, toCurrency: string): Promise<void> => {
    await apiDelete(`${API_BASE}/fx-rate-policies/${fromCurrency}/${toCurrency}`);
  },

  setDefaultMarkup: async (
    data: FxDefaultMarkupUpdate,
  ): Promise<DataResponse<FxDefaultMarkupUpdate>> => {
    return apiPut<DataResponse<FxDefaultMarkupUpdate>>(
      `${API_BASE}/fx-rate-policies/default-markup`,
      data,
    );
  },
};

// ============== Reports ==============

export const reportApi = {
  getTrialBalance: async (params?: {
    as_of_date?: string;
    /** Period start. With it the report carries opening balances + movement. */
    start_date?: string;
    include_zero_balances?: boolean;
    consolidated?: boolean;
    reporting_currency?: string;
    dimension_value_id?: string;
    /** Consolidation nets out intra-group activity by default; false shows the group gross. */
    eliminate_intercompany?: boolean;
  }): Promise<DataResponse<TrialBalanceReport>> => {
    const url = `${API_BASE}/reports/trial-balance${buildParams(params)}`;
    return apiGet<DataResponse<TrialBalanceReport>>(url);
  },

  getBalanceSheet: async (
    asOfDate?: string,
    options?: {
      consolidated?: boolean;
      reporting_currency?: string;
      period_start_date?: string;
      eliminate_intercompany?: boolean;
    },
  ): Promise<DataResponse<BalanceSheetReport>> => {
    const url = `${API_BASE}/reports/balance-sheet${buildParams({ as_of_date: asOfDate, ...options })}`;
    return apiGet<DataResponse<BalanceSheetReport>>(url);
  },

  getIncomeStatement: async (
    startDate: string,
    endDate: string,
    options?: {
      consolidated?: boolean;
      reporting_currency?: string;
      eliminate_intercompany?: boolean;
    },
  ): Promise<DataResponse<IncomeStatementReport>> => {
    const url = `${API_BASE}/reports/income-statement${buildParams({ start_date: startDate, end_date: endDate, ...options })}`;
    return apiGet<DataResponse<IncomeStatementReport>>(url);
  },

  /**
   * The standalone intercompany elimination worksheet for a period — the same
   * evidence the consolidated statements embed, addressable on its own so it can
   * be read without first picking a statement. Returns the model directly (not a
   * `DataResponse` envelope), matching the route's `response_model`.
   */
  getIntercompanyEliminations: async (
    startDate: string,
    endDate: string,
    options?: { reporting_currency?: string; company_id?: string },
  ): Promise<ConsolidationEliminationReport> => {
    return apiGet<ConsolidationEliminationReport>(
      `${API_BASE}/reports/intercompany-eliminations${buildParams({ start_date: startDate, end_date: endDate, ...options })}`,
    );
  },

  getGeneralLedger: async (
    accountId: string,
    startDate: string,
    endDate: string,
    dimensionValueId?: string
  ): Promise<DataResponse<GeneralLedgerReport>> => {
    const url = `${API_BASE}/reports/general-ledger${buildParams({
      account_id: accountId,
      start_date: startDate,
      end_date: endDate,
      dimension_value_id: dimensionValueId,
    })}`;
    return apiGet<DataResponse<GeneralLedgerReport>>(url);
  },
};

// ============== Analytic Dimensions ==============

export interface ListDimensionsParams {
  include_inactive?: boolean;
  applies_to?: 'je' | 'bill';
  company_id?: string;
}

export const dimensionApi = {
  list: async (params?: ListDimensionsParams): Promise<PaginatedResponse<Dimension>> => {
    return apiGet<PaginatedResponse<Dimension>>(`${API_BASE}/dimensions${buildParams(params)}`);
  },

  get: async (id: string): Promise<DataResponse<Dimension>> =>
    apiGet<DataResponse<Dimension>>(`${API_BASE}/dimensions/${id}`),

  create: async (data: DimensionCreate): Promise<DataResponse<Dimension>> =>
    apiPost<DataResponse<Dimension>>(`${API_BASE}/dimensions`, data),

  update: async (id: string, data: DimensionUpdate): Promise<DataResponse<Dimension>> =>
    apiPut<DataResponse<Dimension>>(`${API_BASE}/dimensions/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/dimensions/${id}`);
  },

  listValues: async (
    dimensionId: string,
    includeInactive?: boolean,
  ): Promise<PaginatedResponse<DimensionValue>> => {
    return apiGet<PaginatedResponse<DimensionValue>>(
      `${API_BASE}/dimensions/${dimensionId}/values${buildParams({ include_inactive: includeInactive })}`,
    );
  },

  createValue: async (
    dimensionId: string,
    data: DimensionValueCreate,
  ): Promise<DataResponse<DimensionValue>> =>
    apiPost<DataResponse<DimensionValue>>(`${API_BASE}/dimensions/${dimensionId}/values`, data),

  updateValue: async (
    valueId: string,
    data: DimensionValueUpdate,
  ): Promise<DataResponse<DimensionValue>> =>
    apiPut<DataResponse<DimensionValue>>(`${API_BASE}/dimension-values/${valueId}`, data),

  deleteValue: async (valueId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/dimension-values/${valueId}`);
  },
};

// ============== Default-Dimension Rules (P9) ==============

export const dimensionDefaultRuleApi = {
  list: async (
    params?: ListDimensionDefaultRulesParams,
  ): Promise<PaginatedResponse<DimensionDefaultRule>> => {
    return apiGet<PaginatedResponse<DimensionDefaultRule>>(
      `${API_BASE}/dimension-default-rules${buildParams(params)}`,
    );
  },

  get: async (id: string): Promise<DataResponse<DimensionDefaultRule>> =>
    apiGet<DataResponse<DimensionDefaultRule>>(`${API_BASE}/dimension-default-rules/${id}`),

  create: async (data: DimensionDefaultRuleCreate): Promise<DataResponse<DimensionDefaultRule>> =>
    apiPost<DataResponse<DimensionDefaultRule>>(`${API_BASE}/dimension-default-rules`, data),

  update: async (
    id: string,
    data: DimensionDefaultRuleUpdate,
  ): Promise<DataResponse<DimensionDefaultRule>> =>
    apiPut<DataResponse<DimensionDefaultRule>>(`${API_BASE}/dimension-default-rules/${id}`, data),

  delete: async (id: string): Promise<void> => {
    await apiDelete(`${API_BASE}/dimension-default-rules/${id}`);
  },
};

// ============== FX Revaluation ==============

export const fxRevaluationApi = {
  /** One PAGE of runs. `total` counts the whole filtered set — runs accumulate
   *  one row per fiscal period per company (Shape A), so the pager reads it and
   *  never `data.length`. */
  list: async (
    params?: ListFxRevaluationsParams,
  ): Promise<FxRevaluationListResponse> => {
    return apiGet<FxRevaluationListResponse>(
      `${API_BASE}/fx-revaluations${buildParams(params)}`,
    );
  },

  get: async (id: string): Promise<DataResponse<CurrencyRevaluation>> =>
    apiGet<DataResponse<CurrencyRevaluation>>(`${API_BASE}/fx-revaluations/${id}`),

  run: async (fiscalPeriodId: string): Promise<DataResponse<CurrencyRevaluation>> =>
    apiPost<DataResponse<CurrencyRevaluation>>(`${API_BASE}/fx-revaluations`, {
      fiscal_period_id: fiscalPeriodId,
    }),
};

// ============== Friendly Reports ==============

export const friendlyReportApi = {
  getInvoiceSummary: async (params: FriendlyReportParams): Promise<DataResponse<InvoiceSummaryReportType>> => {
    return apiGet<DataResponse<InvoiceSummaryReportType>>(`${API_BASE}/friendly-reports/invoice-summary${buildParams(params)}`);
  },

  getBillSummary: async (params: FriendlyReportParams): Promise<DataResponse<BillSummaryReportType>> => {
    return apiGet<DataResponse<BillSummaryReportType>>(`${API_BASE}/friendly-reports/bill-summary${buildParams(params)}`);
  },

  getProfitLoss: async (params: FriendlyReportParams): Promise<DataResponse<ProfitLossReportType>> => {
    return apiGet<DataResponse<ProfitLossReportType>>(`${API_BASE}/friendly-reports/profit-loss${buildParams(params)}`);
  },

  getTrend: async (params: TrendReportParams): Promise<DataResponse<TrendReportData>> => {
    return apiGet<DataResponse<TrendReportData>>(`${API_BASE}/friendly-reports/trend${buildParams(params)}`);
  },
};

// ============== Advanced Reports ==============

export const advancedReportApi = {
  getSummarizedIncome: async (params: AdvancedReportParams): Promise<DataResponse<SummarizedIncomeReport>> =>
    apiGet(`${API_BASE}/reports/summarized-income${buildParams(params)}`),

  getSummarizedExpenses: async (params: AdvancedReportParams): Promise<DataResponse<SummarizedExpensesReport>> =>
    apiGet(`${API_BASE}/reports/summarized-expenses${buildParams(params)}`),

  getIncomeByCustomer: async (params: AdvancedReportParams): Promise<DataResponse<IncomeByCustomerReport>> =>
    apiGet(`${API_BASE}/reports/income-by-customer${buildParams(params)}`),

  getExpensesByVendor: async (params: AdvancedReportParams): Promise<DataResponse<ExpensesByVendorReport>> =>
    apiGet(`${API_BASE}/reports/expenses-by-vendor${buildParams(params)}`),

  getExpensesAndPaymentsByVendor: async (params: AdvancedReportParams): Promise<DataResponse<ExpensesAndPaymentsByVendorReport>> =>
    apiGet(`${API_BASE}/reports/expenses-and-payments-by-vendor${buildParams(params)}`),

  getCollection: async (params: AdvancedReportParams & { customer_id?: string; document_type?: string }): Promise<DataResponse<CollectionReport>> => {
    return apiGet(`${API_BASE}/reports/collection${buildParams(params)}`);
  },

  getCollectionSummary: async (params: AdvancedReportParams): Promise<DataResponse<CollectionSummaryReport>> => {
    return apiGet(
      `${API_BASE}/reports/collection${buildParams({ ...params, show_summary_only: true })}`,
    );
  },

  getComparativePL: async (params: { mode: string; year?: number; years?: number[] }): Promise<DataResponse<ComparativePLReport>> => {
    // `years` is a COMMA-SEPARATED STRING server-side (`years: str | None`), not a
    // repeated key — so it is joined here rather than handed to the array branch
    // of buildParams, which would emit `years=2024&years=2025` and lose all but
    // the first.
    return apiGet(
      `${API_BASE}/reports/comparative-profit-loss${buildParams({
        mode: params.mode,
        year: params.year,
        years: params.years?.join(','),
      })}`,
    );
  },

  getVAT: async (params: AdvancedReportParams): Promise<DataResponse<VATReport>> =>
    apiGet(`${API_BASE}/reports/vat${buildParams(params)}`),

  getWithholdingTaxCustomers: async (params: AdvancedReportParams): Promise<DataResponse<WithholdingTaxCustomerReport>> =>
    apiGet(`${API_BASE}/reports/withholding-tax-customers${buildParams(params)}`),

  getWithholdingTaxVendors: async (params: AdvancedReportParams): Promise<DataResponse<WithholdingTaxVendorReport>> =>
    apiGet(`${API_BASE}/reports/withholding-tax-vendors${buildParams(params)}`),

  getExpenseWithholdingSummary: async (params: AdvancedReportParams): Promise<DataResponse<ExpenseWithholdingSummaryReport>> =>
    apiGet(`${API_BASE}/reports/expense-withholding-summary${buildParams(params)}`),

  getPL6111: async (year: number): Promise<DataResponse<PL6111Report>> =>
    apiGet(`${API_BASE}/reports/profit-loss-6111?year=${year}`),

  getCashFlow: async (params: AdvancedReportParams): Promise<DataResponse<CashFlowStatementReport>> =>
    apiGet(`${API_BASE}/reports/cash-flow${buildParams(params)}`),

  getCashFlowTimeseries: async (params: AdvancedReportParams): Promise<DataResponse<CashFlowTimeseriesReport>> =>
    apiGet(`${API_BASE}/reports/cash-flow-timeseries${buildParams(params)}`),

  exportReport: (reportName: string, format: 'pdf' | 'xlsx' | 'csv', params: Record<string, string>): string => {
    return `${API_BASE}/reports/${reportName}/export${buildParams({ ...params, format })}`;
  },
};

// ============== Document Ingestion ==============

const INGESTION_BASE = '/api/v1/accounting/ingestion';

export interface OrganizationSearchResult {
  id: string;
  name: string;
  domain: string | null;
  aliases: string[];
  /**
   * Last 4 digits of the organization's tax id. The full value is encrypted at
   * rest and the read model has never returned it — it used to answer a
   * constant-null `tax_id`, which is now gone (WI 4f52719e).
   */
  tax_id_last4: string | null;
  match_score: number;
}

export const ingestionApi = {
  createRule: (data: IngestionRuleCreate) =>
    apiPost<DataResponse<IngestionRule>>(`${INGESTION_BASE}/rules`, data),

  listRules: (params?: { is_active?: boolean }) => {
    return apiGet<{ data: IngestionRule[] }>(`${INGESTION_BASE}/rules${buildParams(params)}`);
  },

  updateRule: (ruleId: string, data: Partial<IngestionRuleCreate> & { is_active?: boolean }) =>
    apiPatch<DataResponse<IngestionRule>>(`${INGESTION_BASE}/rules/${ruleId}`, data),

  deleteRule: (ruleId: string) =>
    apiDelete(`${INGESTION_BASE}/rules/${ruleId}`),
};

// ============== Vendor classification rules (Tier 2 — defaults) ==============

/**
 * "Given this vendor, pre-fill these fields." Tier 2 of the ingestion rule
 * taxonomy (docs/platform/learned-match-rules-design.md § "Rule taxonomy"):
 * defaults only — since Phase 5a these rules no longer assert vendor IDENTITY,
 * which is single-sourced to Tier 1.
 *
 * The API existed with zero frontend consumers until Phase 5c, so these rules
 * silently drove defaults on every future document from a vendor with no way to
 * see or undo them. Of the stored defaults, only `default_account_id`,
 * `default_tax_rate_id` and `je_detail_level` are applied on the ingestion path;
 * `default_document_type` and the payment-terms columns have no consumer there.
 */
export interface VendorClassificationRule {
  id: string;
  company_id: string;
  vendor_name_pattern: string;
  vendor_email_pattern: string | null;
  vendor_tax_id: string | null;
  organization_id: string | null;
  default_account_id: string;
  default_tax_rate_id: string | null;
  // The GENERATED union, not a hand-written one: the column's vocabulary is
  // single-sourced in `shared/config/vendor_rule_document_type.config.json`, so a
  // literal copy here would be the drift `check_handwritten_enum_union.py` exists to
  // stop — which is also why the sibling `je_detail_level` above is still the local
  // `JeDetailLevel` alias (that vocabulary is not registered yet).
  default_document_type: VendorRuleDocumentType;
  default_payment_terms_id: string | null;
  default_payment_terms_basis: string | null;
  default_payment_terms_days: number | null;
  je_detail_level: JeDetailLevel;
  times_used: number;
  last_used_at: string | null;
  is_active: boolean;
  /**
   * Display names resolved SERVER-side by `GET /vendor-rules` (`_enrich_vendor_rules`).
   *
   * They are not decoration: the page used to look each id up in its own
   * company-scoped `useAccounts()` list, so a rule pointing at another company's
   * GL account rendered an em dash — a rule that reads as "no default" while it is
   * quietly pre-filling one. Null means the record itself is gone, which the UI
   * says in words. Optional only for a response cached before this shipped.
   */
  organization_name?: string | null;
  default_account_name?: string | null;
  default_tax_rate_name?: string | null;
}

/**
 * The update body is the GENERATED type — `vendor_rule_update_request.schema.json`
 * is the source of truth, so re-declaring these fields by hand is how the client
 * drifts from the API (and is what let `je_detail_level` be silently dropped before
 * it was added to that schema). Re-exported so call sites import it from one place.
 */
export type { VendorRuleUpdateRequest };

const VENDOR_RULES_BASE = '/api/v1/accounting/vendor-rules';

/**
 * Thin service over the vendor-rules routes, in the same shape as the sibling
 * `learnedMatchApi`: the LIST route carries no `response_model`, so orval types its
 * return as `unknown` and cannot give us typed rows — hence the hand-written
 * `VendorClassificationRule` row type above and the direct axios calls that keep the
 * compact `{ data }` envelope. The REQUEST type is generated (see above); only the
 * un-modelled response shape is local.
 */
export const vendorRulesApi = {
  list: (params?: { is_active?: boolean }) => {
    // The backend defaults is_active to null (both states) so the management surface
    // can show deactivated rules; pass the filter only when narrowing.
    return apiGet<{ data: VendorClassificationRule[] }>(
      `${VENDOR_RULES_BASE}${buildParams(params)}`,
    );
  },

  update: (ruleId: string, data: VendorRuleUpdateRequest) =>
    apiPut<DataResponse<VendorClassificationRule>>(`${VENDOR_RULES_BASE}/${ruleId}`, data),

  delete: (ruleId: string) => apiDelete(`${VENDOR_RULES_BASE}/${ruleId}`),
};

// ============== Ingestion Rules ==============

export type IngestionRuleConditionField =
  | 'payment_card_last4'
  | 'payment_method'
  | 'vendor_name'
  | 'description'
  | 'currency'
  | 'amount'
  | 'document_type';

export type IngestionRuleConditionOperator =
  | 'equals'
  | 'contains'
  | 'greater_than'
  | 'less_than';

/**
 * Author-only. 'suggested' / 'auto_learned' were dropped in Phase 5c — nothing
 * ever produced them, and a Tier-3 rule is hand-written by definition (learning
 * lives in Tier 1). See docs/platform/learned-match-rules-design.md
 * § "Provenance is a column, not a table".
 */
export type IngestionRuleSource = 'user';

export interface IngestionRuleCondition {
  field: IngestionRuleConditionField;
  operator: IngestionRuleConditionOperator;
  value: string | number;
}

export interface IngestionRuleActions {
  set_payment_account_id?: string;
  set_account_id?: string;
  set_tax_rate_id?: string;
  set_document_type?: DocumentType;
  set_je_detail_level?: JeDetailLevel;
}

/**
 * A rule's actions as STORED. `set_company_id` (assign the vendor organization)
 * was retired in Phase 5a of the learned-match design — identity is single-sourced
 * to Tier 1 — so it is no longer authorable and the backend rejects it on write.
 * Rows created before that still carry the key: it is inert, and the edit dialog
 * strips it so a re-save cleans it up instead of failing validation.
 */
export interface StoredIngestionRuleActions extends IngestionRuleActions {
  set_company_id?: string;
}

export interface IngestionRule {
  id: string;
  name: string;
  description: string | null;
  conditions: IngestionRuleCondition[];
  actions: IngestionRuleActions;
  priority: number;
  auto_approve: boolean;
  source: IngestionRuleSource;
  times_applied: number;
  is_active: boolean;
}

export interface IngestionRuleCreate {
  name: string;
  conditions: IngestionRuleCondition[];
  actions: IngestionRuleActions;
  description?: string;
  priority?: number;
  auto_approve?: boolean;
  /** When set, re-apply the new rule across the still-unmatched items in this open batch. */
  batch_id?: string;
}

// ============== Revenue Category Rules ==============

export interface RevenueCategoryRule {
  id: string;
  tenant_id: string;
  account_id: string;
  entity_type: 'product' | 'product_category' | 'customer';
  entity_id: string;
  /**
   * DERIVED, read-only: the customer organisation's display name on a `customer`
   * rule, resolved batched server-side; `null` on a product or category rule (whose
   * label comes from its own bounded reference list) and on an organisation that no
   * longer resolves. The Setup page used to label these chips out of
   * `useOrganizations({ …, limit: 500 })` and fall back to `entity_id`, so past a
   * tenant's 500th organisation the mapping list read as raw UUIDs (WI 92a581f3).
   */
  customer_name?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const revenueCategoryApi = {
  list: (params?: { entity_type?: string; account_id?: string }): Promise<{ data: RevenueCategoryRule[] }> => {
    return apiGet(`${API_BASE}/revenue-category-rules${buildParams(params)}`);
  },

  create: (data: { account_id: string; entity_type: string; entity_id: string }): Promise<{ data: RevenueCategoryRule }> => {
    return apiPost(`${API_BASE}/revenue-category-rules`, data);
  },

  bulkUpsert: (data: { account_id: string; entity_type: string; entity_ids: string[] }): Promise<{ data: RevenueCategoryRule[] }> => {
    return apiPost(`${API_BASE}/revenue-category-rules/bulk`, data);
  },

  delete: (ruleId: string): Promise<void> => {
    return apiDelete(`${API_BASE}/revenue-category-rules/${ruleId}`);
  },

  resolve: (data: {
    product_id?: string;
    product_category_id?: string;
    invoice_level_account_id?: string;
    customer_id?: string;
  }): Promise<{ data: { account_id: string | null } }> => {
    return apiPost(`${API_BASE}/revenue-category-rules/resolve`, data);
  },
};

// ============== Expense Category Rules ==============

export interface ExpenseCategoryRule {
  id: string;
  tenant_id: string;
  account_id: string;
  entity_type: 'product' | 'product_category' | 'vendor';
  entity_id: string;
  /** DERIVED, read-only: the vendor organisation's name. Same contract as
   *  `RevenueCategoryRule.customer_name`. */
  vendor_name?: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const expenseCategoryApi = {
  list: (params?: { entity_type?: string; account_id?: string }): Promise<{ data: ExpenseCategoryRule[] }> => {
    return apiGet(`${API_BASE}/expense-category-rules${buildParams(params)}`);
  },

  create: (data: { account_id: string; entity_type: string; entity_id: string }): Promise<{ data: ExpenseCategoryRule }> => {
    return apiPost(`${API_BASE}/expense-category-rules`, data);
  },

  bulkUpsert: (data: { account_id: string; entity_type: string; entity_ids: string[] }): Promise<{ data: ExpenseCategoryRule[] }> => {
    return apiPost(`${API_BASE}/expense-category-rules/bulk`, data);
  },

  delete: (ruleId: string): Promise<void> => {
    return apiDelete(`${API_BASE}/expense-category-rules/${ruleId}`);
  },

  resolve: (data: {
    product_id?: string;
    product_category_id?: string;
    bill_level_account_id?: string;
    vendor_id?: string;
  }): Promise<{ data: { account_id: string | null } }> => {
    return apiPost(`${API_BASE}/expense-category-rules/resolve`, data);
  },
};

// ============== Organization Fuzzy Search (from contacts) ==============

const CONTACTS_BASE = '/api/v1/master-data/contacts';

export const organizationSearchApi = {
  fuzzySearch: (query: string, limit = 10) => {
    return apiGet<{ data: OrganizationSearchResult[] }>(
      `${CONTACTS_BASE}/organizations/fuzzy-search${buildParams({ q: query, limit })}`,
    );
  },
};
